import React, { useState, useEffect } from 'react';
import ApiClient from '../../../methods/api/apiClient';
import { toast } from 'react-toastify';

type Method = 'none' | 'email' | 'authenticator';

const OPTIONS: { value: Method; label: string; desc: string }[] = [
    { value: 'none', label: 'Off', desc: 'No second step. Sign in with just your password.' },
    { value: 'email', label: 'Email OTP', desc: 'Receive a one-time code by email each time you log in.' },
    { value: 'authenticator', label: 'Authenticator app', desc: 'Use an app like Google Authenticator or Authy. You’ll scan a QR code the next time you log in.' },
];

const TwoFactorAuth: React.FC = () => {
    const [method, setMethod] = useState<Method>('none');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        ApiClient.get('api/2fa/status').then((res: any) => {
            setLoading(false);
            if (res.success) setMethod(res.method || 'none');
        });
    }, []);

    const handleSelect = (newMethod: Method) => {
        if (newMethod === method || saving) return;
        const prev = method;
        setMethod(newMethod);
        setSaving(true);
        ApiClient.post('api/2fa/set-method', { method: newMethod }).then((res: any) => {
            setSaving(false);
            if (res.success) {
                if (newMethod === 'authenticator') {
                    toast.success('Authenticator selected. Scan the QR code the next time you log in.');
                } else if (newMethod === 'email') {
                    toast.success('Email OTP enabled');
                } else {
                    toast.success('Two-step verification turned off');
                }
            } else {
                setMethod(prev);
                toast.error(res.message || 'Failed to update setting');
            }
        });
    };

    if (loading) return <div className="p-4 text-gray-500">Loading...</div>;

    return (
        <div className="pprofile1 p-6">
            <h3 className="font-semibold mb-2">Two-Step Verification</h3>
            <p className="text-sm text-gray-500 mb-4">
                Choose how you want to verify your identity when logging in.
            </p>
            <div className="space-y-3 max-w-lg">
                {OPTIONS.map(opt => (
                    <label
                        key={opt.value}
                        className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                            method === opt.value ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:bg-gray-50'
                        } ${saving ? 'opacity-60 pointer-events-none' : ''}`}
                    >
                        <input
                            type="radio"
                            name="twofa-method"
                            className="mt-1 h-4 w-4 accent-orange-500"
                            checked={method === opt.value}
                            onChange={() => handleSelect(opt.value)}
                        />
                        <div>
                            <div className="text-sm font-medium text-gray-800">{opt.label}</div>
                            <div className="text-xs text-gray-500">{opt.desc}</div>
                        </div>
                    </label>
                ))}
            </div>
        </div>
    );
};

export default TwoFactorAuth;
